// @ts-nocheck

class ProductGallery {
  constructor() {
    this.galleryEl = document.querySelector('[data-product-gallery]');
    if (!this.galleryEl) return;

    this.containerEl = this.galleryEl.parentElement;
    this.videos = Array.from(this.galleryEl.querySelectorAll('video'));
    this.dotsTrackEl = this.containerEl.querySelector('[data-gallery-dots-track]');
    this.dots = Array.from(this.containerEl.querySelectorAll('[data-gallery-dot]'));
    this.observer = null;
    this.activeIndex = 0;
    this.cursorEl = null;
    this.zoomScale = 4;

    this.scrollRAF = null;
    this.scrollTick = false;
    this.fallbackWidths = [180, 360, 540, 720, 900, 1080, 1296, 1512, 1728, 2048];

    this.setupAutoplay();
    this.setupToggles();
    this.setupDots();
    this.setupLightboxSync();
    this.setupVideoClickGuard();
    this.setupZoom();
    this.setupScrollbar();
    this.setupLazyLoad();
  }

  // The gallery is its own overflow scroller. lazysizes detects "near viewport"
  // off window scroll events, which don't fire from a nested scroller — and its
  // auto-width sizing can resolve the `{width}` RIAS token against a 0-width
  // cell, producing a 404 (the broken-image icon). So we take the gallery's
  // images off lazysizes and load them on intersection, building a concrete
  // srcset from the `{width}` template ourselves.
  setupLazyLoad() {
    this.lazyImages = Array.from(this.galleryEl.querySelectorAll('img.image__img'));
    if (this.lazyImages.length === 0) return;

    if (!('IntersectionObserver' in window)) {
      this.lazyImages.forEach((img) => this.loadImage(img));
      return;
    }

    this.lazyObserver = new IntersectionObserver(
      (entries) => this.onLazyIntersect(entries),
      { root: null, rootMargin: '600px' }
    );
    this.lazyImages.forEach((img) => this.lazyObserver.observe(img));
  }

  onLazyIntersect(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      this.loadImage(entry.target);
      this.lazyObserver.unobserve(entry.target);
    });
  }

  // Resolve the responsive `{width}` URL template to a real srcset so the image
  // can never be left as an unsubstituted (404-ing) source.
  loadImage(img) {
    const template = this.getTemplate(img);
    if (!template) return;

    const widths = this.getWidths(img);
    const cellWidth = Math.round(img.getBoundingClientRect().width) || 720;
    const targetWidth = cellWidth * (window.devicePixelRatio || 1);
    const target = widths.find((w) => w >= targetWidth) || widths[widths.length - 1];

    img.srcset = widths.map((w) => template.replace('{width}', w) + ' ' + w + 'w').join(', ');
    img.sizes = cellWidth + 'px';
    img.src = template.replace('{width}', target);

    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
    img.removeAttribute('data-sizes');
    img.classList.remove('lazyload', 'lazyloading', 'lazyautosizes');
    img.classList.add('lazyloaded');
  }

  // The `{width}`-bearing URL, whether still on data-src or already (badly)
  // copied onto src/srcset by lazysizes. Null once it's a resolved URL.
  getTemplate(img) {
    if (img.dataset.src && img.dataset.src.indexOf('{width}') !== -1) return img.dataset.src;
    if (img.srcset && img.srcset.indexOf('{width}') !== -1) return img.srcset.trim().split(/\s+/)[0];
    if (img.getAttribute('src') && img.getAttribute('src').indexOf('{width}') !== -1) {
      return img.getAttribute('src');
    }
    return null;
  }

  getWidths(img) {
    try {
      const parsed = JSON.parse(img.dataset.widths || '');
      if (Array.isArray(parsed) && parsed.length) return parsed;
    } catch (error) {
      // fall through to defaults
    }
    return this.fallbackWidths;
  }

  setupScrollbar() {
    if (!this.isDesktop()) return;

    this.scrollbarEl = this.containerEl.querySelector('[data-gallery-scrollbar]');
    if (!this.scrollbarEl) return;
    this.railEl = this.scrollbarEl.parentElement;
    this.thumbEl = this.scrollbarEl.querySelector('[data-scroll-thumb]');
    this.trackEl = this.scrollbarEl.querySelector('.gallery-scrollbar__track');
    this.upBtn = this.scrollbarEl.querySelector('[data-scroll-up]');
    this.downBtn = this.scrollbarEl.querySelector('[data-scroll-down]');

    this.galleryEl.addEventListener('scroll', () => this.requestScrollbarUpdate(), { passive: true });
    window.addEventListener('resize', () => this.requestScrollbarUpdate(), { passive: true });
    this.bindScrollButton(this.upBtn, -1);
    this.bindScrollButton(this.downBtn, 1);

    this.updateScrollbar();
  }

  requestScrollbarUpdate() {
    if (this.scrollTick) return;
    this.scrollTick = true;
    requestAnimationFrame(() => {
      this.scrollTick = false;
      this.updateScrollbar();
    });
  }

  updateScrollbar() {
    const el = this.galleryEl;
    const overflow = el.scrollHeight - el.clientHeight;

    if (overflow <= 0) {
      this.railEl.style.display = 'none';
      return;
    }
    this.railEl.style.display = '';

    const pad = 18;
    const railHeight = this.trackEl.clientHeight - pad * 2;

    const thumbHeight = Math.max(railHeight * Math.min(el.clientHeight / el.scrollHeight, 0.9), 24);
    this.thumbEl.style.height = thumbHeight + 'px';

    const progress = Math.min(Math.max(el.scrollTop / overflow, 0), 1);
    this.thumbEl.style.top = pad + progress * (railHeight - thumbHeight) + 'px';

    this.upBtn.disabled = progress <= 0.001;
    this.downBtn.disabled = progress >= 0.999;
  }

  bindScrollButton(btn, direction) {
    btn.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      if (btn.setPointerCapture) btn.setPointerCapture(event.pointerId);
      this.startScroll(direction);
    });
    btn.addEventListener('pointerup', () => this.stopScroll());
    btn.addEventListener('pointercancel', () => this.stopScroll());
  }

  startScroll(direction) {
    this.stopScroll();
    const el = this.galleryEl;
    const step = () => {
      const overflow = el.scrollHeight - el.clientHeight;
      const atBottom = direction > 0 && el.scrollTop >= overflow - 1;
      const atTop = direction < 0 && el.scrollTop <= 1;
      if (atBottom || atTop) {
        this.stopScroll();
        return;
      }
      el.scrollBy(0, direction * 11);
      this.scrollRAF = requestAnimationFrame(step);
    };
    step();
  }

  stopScroll() {
    if (this.scrollRAF) {
      cancelAnimationFrame(this.scrollRAF);
      this.scrollRAF = null;
    }
  }

  setupVideoClickGuard() {
    this.galleryEl.addEventListener('click', (event) => this.onMediaClick(event), true);
  }

  onMediaClick(event) {
    if (event.target.closest('[data-gallery-autoplay]')) return;

    const wrapper = event.target.closest('[data-product-media-wrapper]');
    if (!wrapper) return;
    const media = wrapper.querySelector('.media');
    if (media && media.dataset.mediaType === 'video') {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  isDesktop() {
    return window.matchMedia('(min-width: 60em)').matches;
  }

  setupZoom() {
    if (!this.isDesktop()) return;

    this.images = Array.from(
      this.galleryEl.querySelectorAll('.media[data-media-type="image"] .media__image')
    );
    if (this.images.length === 0) return;

    this.createCursor();
    this.images.forEach((link) => {
      link.addEventListener('mouseenter', () => {
        this.cursorEl.classList.add('is-visible');
        this.upgradeSizes(link);
      });
      link.addEventListener('mousemove', (event) => this.onZoomMove(event, link));
      link.addEventListener('mouseleave', () => this.onZoomLeave(link));
    });
    this.galleryEl.addEventListener('click', (event) => this.onZoomClick(event), true);
  }

  createCursor() {
    this.cursorEl = document.createElement('div');
    this.cursorEl.className = 'gallery-cursor';
    this.cursorEl.setAttribute('aria-hidden', 'true');
    document.body.appendChild(this.cursorEl);
  }

  onZoomMove(event, link) {
    this.cursorEl.style.left = event.clientX + 'px';
    this.cursorEl.style.top = event.clientY + 'px';
    if (link.dataset.zoomed === 'true') {
      this.setOrigin(event, link);
    }
  }

  onZoomLeave(link) {
    this.cursorEl.classList.remove('is-visible', 'is-zoomed');
    this.resetZoom(link);
  }

  onZoomClick(event) {
    const link = event.target.closest('.media__image');
    if (!link || !this.galleryEl.contains(link)) return;
    const media = link.closest('.media');
    if (!media || media.dataset.mediaType !== 'image') return;

    event.preventDefault();
    event.stopPropagation();

    if (link.dataset.zoomed === 'true') {
      this.resetZoom(link);
      this.cursorEl.classList.remove('is-zoomed');
    } else {
      this.applyZoom(event, link);
      this.cursorEl.classList.add('is-zoomed');
    }
  }

  setOrigin(event, link) {
    const img = link.querySelector('.image__img');
    if (!img) return;
    const rect = link.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    img.style.transformOrigin = x + '% ' + y + '%';
  }

  applyZoom(event, link) {
    const img = link.querySelector('.image__img');
    if (!img) return;
    this.upgradeSizes(link);
    this.setOrigin(event, link);
    img.style.transform = 'scale(' + this.getZoomScale(link) + ')';
    link.dataset.zoomed = 'true';
  }

  // Zoom only as hard as the loaded source stays sharp, so a large cell with a
  // limited-resolution image zooms less instead of stretching. Capped at zoomScale.
  getZoomScale(link) {
    const cellWidth = link.getBoundingClientRect().width;
    const sourceWidth = parseFloat(link.dataset.srcWidth) || 0;
    if (cellWidth <= 0 || sourceWidth <= 0) return this.zoomScale;
    // Prefer a sharp scale, but always zoom at least minZoom (a little blur is ok).
    const minZoom = 2.5;
    return Math.min(this.zoomScale, Math.max(minZoom, sourceWidth / cellWidth));
  }

  // Raise the img's `sizes` so the browser picks the largest source from the
  // existing srcset (Shopify's responsive variants), then record its real
  // resolution so the zoom can scale to it.
  upgradeSizes(link) {
    const img = link.querySelector('.image__img');
    if (!img || img.dataset.zoomReady) return;
    img.dataset.zoomReady = 'true';
    // Stop lazysizes' auto-sizer from resetting `sizes` back to the cell width.
    img.classList.remove('lazyautosizes');
    img.removeAttribute('data-sizes');
    img.addEventListener('load', () => {
      link.dataset.srcWidth = img.naturalWidth;
      if (link.dataset.zoomed === 'true') {
        img.style.transform = 'scale(' + this.getZoomScale(link) + ')';
      }
    });
    img.sizes = Math.round(link.getBoundingClientRect().width * this.zoomScale) + 'px';
    if (img.complete && img.naturalWidth) {
      link.dataset.srcWidth = img.naturalWidth;
    }
  }

  resetZoom(link) {
    const img = link.querySelector('.image__img');
    if (img) {
      img.style.transform = '';
      img.style.transformOrigin = '';
    }
    link.dataset.zoomed = 'false';
  }

  setupAutoplay() {
    if (this.videos.length === 0) return;

    this.videos.forEach((video) => {
      video.muted = true;
      video.setAttribute('playsinline', '');
    });

    if (this.isReducedMotion()) {
      this.videos.forEach((video) => {
        this.pauseVideo(video);
        this.syncToggle(video, false);
      });
      return;
    }

    this.observer = new IntersectionObserver((entries) => this.onIntersect(entries), {
      threshold: 0.5,
    });
    this.videos.forEach((video) => this.observer.observe(video));
  }

  isReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  onIntersect(entries) {
    entries.forEach((entry) => {
      const video = entry.target;
      if (video.dataset.userPaused === 'true') return;
      if (entry.isIntersecting) {
        this.playVideo(video);
      } else {
        this.pauseVideo(video);
      }
    });
  }

  setupToggles() {
    const buttons = this.galleryEl.querySelectorAll('[data-gallery-autoplay]');
    buttons.forEach((btn) => {
      btn.addEventListener('click', () => this.onToggle(btn));
    });
  }

  onToggle(btn) {
    const wrapper = btn.closest('[data-product-media-wrapper]');
    const video = wrapper ? wrapper.querySelector('video') : null;
    if (!video) return;

    const willPlay = video.paused;
    video.dataset.userPaused = willPlay ? 'false' : 'true';
    if (willPlay) {
      this.playVideo(video);
    } else {
      this.pauseVideo(video);
    }
    this.updateToggleUI(btn, willPlay);
  }

  syncToggle(video, isPlaying) {
    const wrapper = video.closest('[data-product-media-wrapper]');
    const btn = wrapper ? wrapper.querySelector('[data-gallery-autoplay]') : null;
    if (btn) this.updateToggleUI(btn, isPlaying);
  }

  updateToggleUI(btn, isPlaying) {
    const pauseIcon = btn.querySelector('[data-icon-pause]');
    const playIcon = btn.querySelector('[data-icon-play]');
    btn.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
    if (pauseIcon) pauseIcon.hidden = !isPlaying;
    if (playIcon) playIcon.hidden = isPlaying;
  }

  playVideo(video) {
    const played = video.play();
    if (played && typeof played.catch === 'function') {
      played.catch(() => {});
    }
  }

  pauseVideo(video) {
    video.pause();
  }

  setupDots() {
    if (this.dots.length < 2) return;

    this.galleryEl.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.dots.forEach((dot) => {
      dot.addEventListener('click', (event) => this.onDotClick(event));
    });
    this.activeIndex = Math.round(this.galleryEl.scrollLeft / (this.galleryEl.clientWidth || 1));
    this.updateActiveDot();
  }

  onScroll() {
    const slideWidth = this.galleryEl.clientWidth;
    if (slideWidth === 0) return;
    const index = Math.round(this.galleryEl.scrollLeft / slideWidth);
    if (index !== this.activeIndex) {
      this.activeIndex = index;
      this.updateActiveDot();
    }
  }

  onDotClick(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10);
    this.scrollToIndex(index);
  }

  scrollToIndex(index) {
    this.galleryEl.scrollTo({
      left: index * this.galleryEl.clientWidth,
      behavior: 'smooth',
    });
  }

  updateActiveDot() {
    this.dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === this.activeIndex);
    });
    this.scrollDotsToActive();
  }

  // The track is a horizontal scroller capped at ~7 dots. Centre the active dot so
  // each swipe visibly scrolls the strip; the native scroller clamps at the ends,
  // so the first/last dot stays put when there is nothing more to swipe to that side.
  scrollDotsToActive() {
    if (!this.dotsTrackEl) return;
    const active = this.dots[this.activeIndex];
    if (!active) return;
    const li = active.parentElement || active;
    const center = li.offsetLeft + li.offsetWidth / 2;
    this.dotsTrackEl.scrollTo({ left: center - this.dotsTrackEl.clientWidth / 2, behavior: 'smooth' });
  }

  // The vendor lightbox runs its own slider and never reports back. Watch its
  // classes; when it closes, move the gallery to whichever image was last shown so
  // the slide (and dots) match what the shopper was looking at. The vendor drops
  // 'visible' first (while the lightbox still covers the gallery) then 'active' at
  // the very end of the 300ms close. We sync on BOTH edges: the 'visible' edge
  // moves the gallery behind the still-opaque lightbox for a seamless reveal, and
  // the 'active' edge re-asserts AFTER the focus trap restores focus to the
  // original image (which would otherwise scroll the gallery back).
  setupLightboxSync() {
    this.lightboxEl = document.querySelector('[data-lightbox]');
    if (!this.lightboxEl) return;

    this.lightboxWasVisible = this.lightboxEl.classList.contains('visible');
    this.lightboxWasActive = this.lightboxEl.classList.contains('active');
    this.lightboxObserver = new MutationObserver(() => this.onLightboxToggle());
    this.lightboxObserver.observe(this.lightboxEl, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  onLightboxToggle() {
    const isVisible = this.lightboxEl.classList.contains('visible');
    const isActive = this.lightboxEl.classList.contains('active');
    if ((this.lightboxWasVisible && !isVisible) || (this.lightboxWasActive && !isActive)) {
      this.syncFromLightbox();
    }
    this.lightboxWasVisible = isVisible;
    this.lightboxWasActive = isActive;
  }

  syncFromLightbox() {
    const selected = this.lightboxEl.querySelector('[data-slide].is-selected');
    if (!selected) return;

    const wrappers = Array.from(this.galleryEl.querySelectorAll('[data-product-media-wrapper]'));
    const index = wrappers.findIndex((wrapper) => wrapper.dataset.mediaId === selected.dataset.id);
    if (index < 0) return;

    this.activeIndex = index;
    this.scrollGalleryToWrapper(wrappers[index]);
    requestAnimationFrame(() => this.scrollGalleryToWrapper(wrappers[index]));
    this.updateActiveDot();
  }

  scrollGalleryToWrapper(wrapper) {
    const galleryRect = this.galleryEl.getBoundingClientRect();
    const targetRect = wrapper.getBoundingClientRect();
    if (this.isDesktop()) {
      const top = this.galleryEl.scrollTop + (targetRect.top - galleryRect.top);
      this.galleryEl.scrollTo({ top, behavior: 'auto' });
    } else {
      const left = this.galleryEl.scrollLeft + (targetRect.left - galleryRect.left);
      this.galleryEl.scrollTo({ left, behavior: 'auto' });
    }
  }
}

new ProductGallery();
